import { createContext, useCallback, useContext, useMemo, useState, type ReactElement, type ReactNode } from 'react';

interface TopNavSlotValue {
    node: HTMLDivElement | null;
    setNode: (node: HTMLDivElement | null) => void;
}

const TopNavSlotContext = createContext<TopNavSlotValue | null>(null);

/** Hosts the shared mount point that lets page content render into the shell's top nav. */
export function TopNavSlotProvider({ children }: { children: ReactNode }): ReactElement {
    const [node, setNode] = useState<HTMLDivElement | null>(null);
    const value = useMemo(() => ({ node, setNode }), [node]);
    return <TopNavSlotContext.Provider value={value}>{children}</TopNavSlotContext.Provider>;
}

/** The top nav's mount point for the active page's toolbar (portalled in by PageLevelFilterRow). */
export function TopNavSlotOutlet(): ReactElement {
    const setNode = useContext(TopNavSlotContext)?.setNode;
    const attach = useCallback((element: HTMLDivElement | null) => setNode?.(element), [setNode]);
    return <div ref={attach} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 0, flex: 1 }} />;
}

/** The DOM node pages portal their top-nav toolbar into (null until the shell header mounts). */
export function useTopNavSlotNode(): HTMLDivElement | null {
    return useContext(TopNavSlotContext)?.node ?? null;
}
